import Link from 'next/link'
import type { MicroCMSImage } from 'microcms-js-sdk'
import { Chip, type ChipSurface } from '@/components/parts/chip'
import { Time } from '@/components/parts/time'
import { getOptimizedImageProps } from '@/utils/image'

type CaseCardImage = {
  src: string
  srcSet?: string
  sizes?: string
  alt?: string
}

type CaseCardProps = {
  href: string
  /** 別サイトの記事（FRAIM のニュースなど）。新しいタブで開く */
  external?: boolean
  /** 見出し（事例のキャッチコピー） */
  title: string
  /** タイトルを 3 行で省略する（Figma のセミナーカード） */
  clampTitle?: boolean
  /** 会社名などの属性行 */
  meta?: string
  /**
   * 業種・導入部署・企業規模・文書類型など。
   * 業種と文書類型の両方が「その他」になるなど label は重複しうるので、
   * どの分類から来たかを group に持たせて鍵を一意にする。
   * surface の既定は beige（白い面の上に置くタグ）。
   */
  tags?: { group: string; label: string; surface?: ChipSurface }[]
  /**
   * タグの下に置く日付と著者の行（Figma card の Date。コラム一覧・ニュース一覧）。
   * date は microCMS の日時（ISO 8601）。著者の無いニュースは author を省く
   */
  byline?: { date: string; author?: string }
  image?: CaseCardImage
}

/**
 * microCMS の画像を CaseCard の image に渡す形に揃える。
 * 画像 URL が空のときは srcSet / sizes を含まない形で返ってくる
 */
export const toCaseCardImage = (
  eyecatch?: MicroCMSImage
): CaseCardImage | undefined => {
  const optimized = eyecatch
    ? getOptimizedImageProps(eyecatch, { quality: 80 })
    : undefined
  if (!optimized?.src) return undefined

  return {
    src: optimized.src,
    srcSet: 'srcSet' in optimized ? (optimized.srcSet as string) : undefined,
    sizes: 'sizes' in optimized ? (optimized.sizes as string) : undefined,
    alt: ''
  }
}

/**
 * 一覧のカード。サムネイル 386×217 + 見出し + 属性 + タグ + 日付・著者。
 * 導入事例・お役立ち資料・セミナー・コラム・ニュースで共通。高さは中身に追従する。
 */
export function CaseCard({
  href,
  external = false,
  title,
  clampTitle = false,
  meta,
  tags = [],
  byline,
  image
}: CaseCardProps) {
  return (
    <li className="flex">
      <Link
        href={href}
        className="group/card flex w-full flex-col gap-5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-line-inverse"
        {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        <div className="aspect-banner w-full overflow-hidden rounded-xl bg-surface-muted">
          {image?.src ? (
            <img
              src={image.src}
              srcSet={image.srcSet}
              sizes={image.sizes}
              alt={image.alt ?? ''}
              loading="lazy"
              className="size-full object-cover transition-transform duration-500 ease-in-out will-change-transform group-hover/card:scale-105"
            />
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          <h3
            className={`text-lg font-bold leading-compact text-ink ${clampTitle ? 'line-clamp-3' : ''}`}
          >
            {title}
            {external ? (
              <span className="sr-only">（新しいタブで開く）</span>
            ) : null}
          </h3>
          {meta ? (
            <p className="text-base leading-body text-ink">{meta}</p>
          ) : null}
          {tags.length > 0 ? (
            <ul className="flex flex-wrap items-center gap-2">
              {tags.map((tag) => (
                <li key={`${tag.group}-${tag.label}`}>
                  <Chip label={tag.label} surface={tag.surface ?? 'beige'} />
                </li>
              ))}
            </ul>
          ) : null}
          {byline ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm leading-compact text-ink">
              <p className="flex items-center gap-1">
                <span
                  aria-hidden="true"
                  className="icon icon-calendar size-6"
                />
                <Time gmt={byline.date} />
              </p>
              {byline.author ? (
                <p className="flex items-center gap-1">
                  <span aria-hidden="true" className="icon icon-user size-6" />
                  <span className="sr-only">著者</span>
                  {byline.author}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </Link>
    </li>
  )
}
